import type {
  TVendorCode,
  TVendorCodeCommercial,
  TVendorCodeCommercialHomeWarranty,
  TVendorCodesInternet,
  TVendorCodesInternetHomeWarranty,
  TVendorCodeCommercialUpsertModel,
} from '../models/vendor/VendorCodeModels';
import { VendorCodeType } from '../enums/vendor/VendorCodeTypeEnum';
import WebAPIClient from './api/WebAPIClient';

const PATH = 'VendorCodes';
const PATH_INTERNET = 'VendorCodes/Internet';
const PATH_INTERNET_HOME_WARRANTY = 'VendorCodes/Internet/Home-Warranty';
const PATH_COMMERCIAL = 'VendorCodes/Commercial';
const PATH_COMMERCIAL_HOME_WARRANTY = 'VendorCodes/Commercial/Home-Warranty';

export const getAllVendorCodes = async (): Promise<Array<TVendorCode>> => {
  const response = await WebAPIClient.get(PATH);
  if (!response?.data) {
    return [];
  }

  const vendorCodeCommercials = (response.data.vendorCodeCommercials ?? []).map(
    (commercialCode) => {
      return { type: VendorCodeType.COMMERCIAL, ...commercialCode };
    },
  );

  const vendorCodeInternets = (response.data.vendorCodeInternets ?? []).map((internetCode) => {
    return { type: VendorCodeType.INTERNET, ...internetCode };
  });

  const vendorCodeCommercialHomeWarranties = (
    response.data.vendorCodeCommercialHomeWarranties ?? []
  ).map((commercialHomeWarrantyCode) => {
    return {
      type: VendorCodeType.COMMERCIAL_HOME_WARRANTY,
      ...commercialHomeWarrantyCode,
    };
  });

  const vendorCodeInternetHomeWarranties = (
    response.data.vendorCodeInternetHomeWarranties ?? []
  ).map((internetHomeWarrantyCode) => {
    return {
      type: VendorCodeType.INTERNET_HOME_WARRANTY,
      ...internetHomeWarrantyCode,
    };
  });

  return vendorCodeCommercials.concat(
    vendorCodeInternets,
    vendorCodeCommercialHomeWarranties,
    vendorCodeInternetHomeWarranties,
  );
};

export const getVendorCodeCommercial = async (
  vendorCodeId: string,
): Promise<TVendorCodeCommercial> => {
  const response = await WebAPIClient.get(`${PATH_COMMERCIAL}/${vendorCodeId}`);

  return response?.data;
};

export const createVendorCodeCommercial = async (
  vendorCodeCommercial: TVendorCodeCommercial,
): Promise<TVendorCodeCommercial | undefined> => {
  const response = await WebAPIClient.post(PATH_COMMERCIAL, vendorCodeCommercial);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.COMMERCIAL, ...response?.data };
};

export const updateVendorCodeCommercial = async (
  vendorCodeCommercial: TVendorCodeCommercial,
): Promise<TVendorCodeCommercial | undefined> => {
  const response = await WebAPIClient.put(PATH_COMMERCIAL, vendorCodeCommercial);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.COMMERCIAL, ...response?.data };
};

export const getVendorCodeCommercialHomeWarranty = async (
  vendorCodeId: string,
): Promise<TVendorCodeCommercialHomeWarranty> => {
  const response = await WebAPIClient.get(`${PATH_COMMERCIAL_HOME_WARRANTY}/${vendorCodeId}`);

  return response?.data;
};

export const createVendorCodeCommercialHomeWarranty = async (
  vendorCodeCommercial: TVendorCodeCommercialHomeWarranty,
): Promise<TVendorCodeCommercialHomeWarranty | undefined> => {
  const response = await WebAPIClient.post(PATH_COMMERCIAL_HOME_WARRANTY, vendorCodeCommercial);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.COMMERCIAL_HOME_WARRANTY, ...response?.data };
};

export const updateVendorCodeCommercialHomeWarranty = async (
  vendorCodeCommercial: TVendorCodeCommercialHomeWarranty,
): Promise<TVendorCodeCommercialHomeWarranty | undefined> => {
  const response = await WebAPIClient.put(PATH_COMMERCIAL_HOME_WARRANTY, vendorCodeCommercial);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.COMMERCIAL_HOME_WARRANTY, ...response?.data };
};

export const getVendorCodesInternet = async (): Promise<Array<TVendorCodesInternet>> => {
  const response = await WebAPIClient.get(PATH_INTERNET);

  return response?.data ?? [];
};

export const getVendorCodeInternet = async (
  vendorCodeId: string,
): Promise<TVendorCodesInternet> => {
  const response = await WebAPIClient.get(`${PATH_INTERNET}/${vendorCodeId}`);

  return response?.data;
};

export const createVendorCodeInternet = async (
  vendorCodeInternet: TVendorCodesInternet,
): Promise<TVendorCodesInternet | undefined> => {
  const response = await WebAPIClient.post(PATH_INTERNET, vendorCodeInternet);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.INTERNET, ...response?.data };
};

export const updateVendorCodeInternet = async (
  vendorCodeInternet: TVendorCodesInternet,
): Promise<TVendorCodesInternet | undefined> => {
  const response = await WebAPIClient.put(PATH_INTERNET, vendorCodeInternet);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.INTERNET, ...response?.data };
};

export const getVendorCodeInternetHomeWarranty = async (
  vendorCodeId: string,
): Promise<TVendorCodesInternetHomeWarranty> => {
  const response = await WebAPIClient.get(`${PATH_INTERNET_HOME_WARRANTY}/${vendorCodeId}`);

  return response?.data;
};

export const createVendorCodeInternetHomeWarranty = async (
  vendorCodeInternet: TVendorCodesInternetHomeWarranty,
): Promise<TVendorCodesInternetHomeWarranty | undefined> => {
  const response = await WebAPIClient.post(PATH_INTERNET_HOME_WARRANTY, vendorCodeInternet);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.INTERNET_HOME_WARRANTY, ...response?.data };
};

export const updateVendorCodeInternetHomeWarranty = async (
  vendorCodeInternet: TVendorCodesInternetHomeWarranty,
): Promise<TVendorCodesInternetHomeWarranty | undefined> => {
  const response = await WebAPIClient.put(PATH_INTERNET_HOME_WARRANTY, vendorCodeInternet);
  if (!response?.data) {
    return undefined;
  }
  return { type: VendorCodeType.INTERNET_HOME_WARRANTY, ...response?.data };
};

export const parseVendorCodesCommercialCsvFile = async (
  file: File,
): Promise<Array<TVendorCodeCommercialUpsertModel>> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await WebAPIClient.post(`${PATH_COMMERCIAL}/parsecsvfile`, formData, false, {
    'content-type': 'multipart/form-data',
  });

  return response?.data;
};

export const upsertVendorCodesCommercial = async (
  vendorCodes: Array<TVendorCodeCommercialUpsertModel>,
): Promise<void> => {
  const response = await WebAPIClient.post(`${PATH_COMMERCIAL}/bulkupsert`, vendorCodes);
  return response?.data;
};
